import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Table,
  Button,
  Dialog,
  Form,
  Input,
  Select,
  DatePicker,
  DateRangePicker,
  Tag,
  Space,
  Popconfirm,
  MessagePlugin,
  Switch,
  type PrimaryTableCol,
} from 'tdesign-react';
import { AddIcon, EditIcon, DeleteIcon, SearchIcon, PlusIcon, UserAddIcon } from 'tdesign-icons-react';
import {
  Plus, Pencil, Trash2, Search, X, Mail, Phone, MapPin, Building2, UserCircle,
} from 'lucide-react';
import { tenantApi, businessCardLevelApi, businessCardApi, userApi } from '../../api';
import CoverImageUploader from '../../components/CoverImageUploader';
import type { Tenant } from '../../types';
import type { CardConfig } from '../../../user/console/eCardManage/types';

const { FormItem } = Form;
const { Option } = Select;

const tenantTypeMap: Record<number, { label: string; theme: 'primary' | 'success' }> = {
  1: { label: '企业', theme: 'primary' },
  2: { label: '个人', theme: 'success' },
};

// 组织类型（仅租户类型为「企业」时生效）
const orgTypeMap: Record<number, { label: string; theme: 'primary' | 'warning' }> = {
  1: { label: '企业', theme: 'primary' },
  2: { label: '政府', theme: 'warning' },
};

const tenantStatusMap: Record<number, { label: string; theme: 'default' | 'success' | 'warning' }> = {
  0: { label: '未开始', theme: 'default' },
  1: { label: '有效', theme: 'success' },
  2: { label: '已过期', theme: 'warning' },
};

/** 微官网展示开关：仅套餐等级（tenantLevel）为 5 或 6 时可选 */
const WEBSITE_SHOW_LEVELS = [5, 6];
const canSelectWebsiteShow = (tenantLevel?: number | null) =>
  tenantLevel != null && WEBSITE_SHOW_LEVELS.includes(Number(tenantLevel));

/** 兼容后端返回的布尔值（true / 1 / '1' / 'true'） */
const toBoolFlag = (value: any) =>
  value === true || value === 1 || value === '1' || value === 'true';

interface PackageLevel {
  id: number;
  levelName: string;
  levelType: number;
  levelOrigPrice: number;
  levelSalesPrice: number;
  levelUserNum: number;
  sortOrder: number;
  status: number;
  /** 套餐默认是否展示微官网 */
  websiteShow?: boolean;
}

interface AdminUser {
  id: number;
  username: string;
  nickname?: string;
  phone?: string;
  email?: string;
}

interface BusinessCardFormState {
  id?: number;
  name: string;
  phone: string;
  position: string;
  email: string;
  company: string;
  address: string;
  status: number;
}

const emptyCardForm: BusinessCardFormState = {
  name: '',
  phone: '',
  position: '',
  email: '',
  company: '',
  address: '',
  status: 1,
};

const normalizeCardLayout = (layout: unknown): CardConfig['cardLayout'] => {
  return layout === 'center' || layout === 'right' || layout === 'preview-4' ? layout : 'left';
};

const toPreviewConfig = (card: any): CardConfig & { fontColor?: string } => {
  const displayFields = Array.isArray(card.cardDisplayFields)
    ? card.cardDisplayFields.reduce((fields: Record<string, boolean>, field: string) => {
      fields[field === 'address' ? 'companyAddress' : field] = true;
      return fields;
    }, {})
    : Array.isArray(card.displayFields)
      ? card.displayFields.reduce((fields: Record<string, boolean>, field: string) => {
        fields[field === 'address' ? 'companyAddress' : field] = true;
        return fields;
      }, {})
      : card.displayFields || {};

  return {
    id: Number(card.id) || 0,
    name: card.name || '',
    logo: card.logo || '',
    pageBg: card.cardPageBackground || card.pageBg || '',
    mainTitle: card.company || card.theme || card.mainTitle || card.name || '',
    subTitle: card.position || card.subTheme || card.subTitle || '',
    subTitleEn: card.subThemeEn || card.subTitleEn || '',
    themeColor: card.themeColor || '#ffffff',
    contact: {
      qrcode: card.contact?.qrCode || card.contact?.qrcode || '',
      address: card.contact?.address || card.address || '',
      phone: card.contact?.phone || card.phone || '',
      email: card.contact?.email || card.email || '',
    },
    govService: {
      serviceTime: card.govService?.serviceTime || '',
      serviceHotlines: card.govService?.serviceHotlines?.length
        ? [...card.govService.serviceHotlines]
        : [''],
      trafficGuides: card.govService?.trafficGuides?.length
        ? [...card.govService.trafficGuides]
        : [''],
      publicAccount: {
        name: card.govService?.publicAccount?.name || '',
        qrcode: card.govService?.publicAccount?.qrcode || '',
      },
      onlineEntries: card.govService?.onlineEntries?.length
        ? card.govService.onlineEntries.map((e: any) => ({ name: e?.name || '', image: e?.image || '' }))
        : [{ name: '', image: '' }],
    },
    unitIntro: {
      unitName: card.unitIntro?.unitName || '',
      unitTypes: card.unitIntro?.unitTypes?.length ? [...card.unitIntro.unitTypes] : [],
      introduction: card.unitIntro?.introduction || '',
      unitPositioning: (card.unitIntro?.unitPositioning || []).map((p: any, i: number) => ({
        id: `unit-pos-${i}`,
        icon: p?.icon || '⭐',
        title: p?.content || '',
      })),
      jurisdictionOverview: card.unitIntro?.jurisdictionOverview || '',
      departments: (card.unitIntro?.departments || []).map((d: any, i: number) => ({
        id: `dept-${i}`,
        title: d?.name || '',
        description: d?.description || '',
        phone: d?.phone || '',
      })),
    },
    displayFields: {
      name: displayFields.name === true,
      position: displayFields.position === true,
      avatar: displayFields.avatar === true,
      phone: displayFields.phone === true,
      email: displayFields.email === true,
      company: displayFields.company === true,
      companyAddress: displayFields.companyAddress === true,
    },
    cardBgType: card.backgroundImage || card.cardBackgroundImage || card.cardBg ? 'custom' : 'default',
    cardBg: card.backgroundImage || card.cardBackgroundImage || card.cardBg || '',
    fontColor: card.fontColor || '#333333',
    cardLayout: normalizeCardLayout(card.contentPosition || card.cardLayout),
    status: 'active',
    createdAt: card.createdAt || card.createTime || '',
    saveToContacts: false,
    enablePoster: false,
    posterBg: '',
    posterCardPosition: { x: 50, y: 50 },
    posterInvitePosition: { x: 50, y: 50 },
    enableMiniCode: false,
    enableShortLink: false,
    selectedPages: [],
    philosophyItems: [],
    coreBusinessItems: [],
    productIntro: [],
    featureServices: (card.featureServices || []).map((p: any, i: number) => ({
      id: `feature-${i}`,
      image: p?.image || '',
      title: p?.title || '',
      description: p?.description || '',
    })),
    companyIntro: {} as CardConfig['companyIntro'],
    companyQualifications: {} as CardConfig['companyQualifications'],
    investmentProjectItems: [],
    companyStrengthItems: [],
    cooperation: { count: 0, logos: [] },
    companyPromo: { images: [], videos: [] },
    visionItems: [],
    supplementInfo: {
      officialWebsite: {
        name: card.supplementInfo?.officialWebsite?.name || '',
        url: card.supplementInfo?.officialWebsite?.url || '',
      },
      socialMediaAccounts: (card.supplementInfo?.socialMediaAccounts || []).map((item: any, i: number) => ({
        id: `social-${i}`,
        image: item?.image || '',
        name: item?.name || '',
      })),
      otherContent: card.supplementInfo?.otherContent || '',
    },
    promoVideo: '',
  };
};

function CardContentPreview({ config }: { config: any }) {
  console.log('CardContentPreview config:', config);

  const contactItems = (includeAddress = true) => (
    <div className="space-y-1 text-sm text-gray-700">
      {config.displayFields.includes('phone') && config.phone && <div className="flex items-center gap-2 truncate">电话：{config.phone || '-'}</div>}
      {config.displayFields.includes('email') && config.email && <div className="flex items-center gap-2 truncate">邮箱：{config.email || '-'}</div>}
      {includeAddress && config.displayFields.includes('address') && config.address && <div className="flex items-center gap-2 truncate">地址：{config.address || '-'}</div>}
    </div>
  );

  if (config.contentPosition === 'center') {
    return (
      <div className="w-full h-full p-3 flex flex-col justify-between">
        <div className="space-y-2">
          {config.name && <div className="text-2xl font-bold text-gray-900">{config.name}</div>}
          {config.displayFields.includes('position') && <div className="text-sm text-gray-700">{config.position || '-'}</div>}
          {config.displayFields.includes('company') && <div className="text-sm text-gray-700">{config.company || '-'}</div>}
        </div>
        <div>{contactItems()}</div>
      </div>
    );
  }

  if (config.contentPosition === 'right') {
    return (
      <div className="w-full h-full p-3 flex flex-col justify-between">
        <div className="flex-1 flex flex-col justify-end items-center space-y-1">
          {config.name && <div className="text-2xl font-bold text-gray-900">{config.name}</div>}
          {config.displayFields.includes('position') && <div className="text-sm text-gray-700">{config.position || '-'}</div>}
          {config.displayFields.includes('company') && <div className="text-sm text-gray-700">{config.company || '-'}</div>}
        </div>
        <div className="border-t border-gray-200 my-2" />
        <div className="flex-1 flex flex-col justify-start items-center space-y-1">
          {config.displayFields.includes('phone') && config.phone && <div className="text-sm text-gray-700 truncate">电话：{config.phone || '-'}</div>}
          {config.displayFields.includes('email') && config.email && <div className="text-sm text-gray-700 truncate">邮箱：{config.email || '-'}</div>}
          { config.displayFields.includes('address') && config.address && <div className="text-sm text-gray-700 truncate">地址：{config.address || '-'}</div>}
        </div>
      </div>
    );
  }

  if (config.contentPosition === 'preview-4') {
     return (
      <div className="w-full h-full p-3 flex flex-col justify-center" >
        <div className="flex-1 flex flex-col justify-end space-y-1">
          {config.name && <div className="text-2xl font-bold text-gray-900 truncate">{config.name}</div>}
          {config.displayFields.includes('position') && config.position && <div className="text-sm text-gray-600 truncate">{config.position || '-'}</div>}
          {config.displayFields.includes('company') && config.company && <div className="text-sm text-gray-700 truncate">{config.company || '-'}</div>}
        </div>
        <div className="border-t border-gray-200 my-2" />
        <div className="flex-1 flex flex-col justify-start">
          {contactItems()}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-3 flex flex-col justify-between" >
      {config.displayFields.includes('company') && config.company && <div className="text-sm text-gray-700 truncate">{config.company || '-'}</div>}
      <div className="space-y-2">
        <div className="flex items-baseline gap-3">
          {config.name && <div className="text-2xl font-bold text-gray-900 truncate">{config.name}</div>}
          {config.displayFields.includes('position') && config.position && <div className="text-base text-gray-600 truncate">{config.position || '-'}</div>}
        </div>
        {contactItems(false)}
      </div>
      {config.displayFields.includes('address') && config.address && <div className="text-sm text-gray-700 truncate">{config.address || '-'}</div>}
    </div>
  );
}

export default function TenantManage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [mergeVisible, setMergeVisible] = useState(false);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [searchForm] = Form.useForm();
  const formInstanceRef = useRef<any>(null);
  const mergeFormInstanceRef = useRef<any>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  /** 新增/编辑弹窗表单中的租户类型，用于控制「组织类型」字段的显示 */
  const [formTenantType, setFormTenantType] = useState<number | undefined>(undefined);
  /** 合并创建弹窗表单中的租户类型，用于控制「组织类型」字段的显示 */
  const [mergeFormTenantType, setMergeFormTenantType] = useState<number | undefined>(undefined);
  /** 新增/编辑弹窗表单中的套餐等级，用于控制「微官网」字段的显示 */
  const [formTenantLevel, setFormTenantLevel] = useState<number | undefined>(undefined);
  /** 合并创建弹窗表单中的套餐等级，用于控制「微官网」字段的显示 */
  const [mergeFormTenantLevel, setMergeFormTenantLevel] = useState<number | undefined>(undefined);
  /** 列表中正在切换微官网开关的租户 id */
  const [websiteTogglingId, setWebsiteTogglingId] = useState<number | null>(null);

  const [packageLevels, setPackageLevels] = useState<PackageLevel[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<PackageLevel | null>(null);
  const [mergeSelectedPackage, setMergeSelectedPackage] = useState<PackageLevel | null>(null);
  const [userSearchKeyword, setUserSearchKeyword] = useState('');
  const [userLoading, setUserLoading] = useState(false);
  const [mergeAvatar, setMergeAvatar] = useState('');

  // 电子名片弹窗状态
  const [cardDialogVisible, setCardDialogVisible] = useState(false);
  const [cardTenant, setCardTenant] = useState<Tenant | null>(null);
  const [cardData, setCardData] = useState<any[]>([]);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardPagination, setCardPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [cardSelectedRowKeys, setCardSelectedRowKeys] = useState<number[]>([]);
  const [cardPreviewVisible, setCardPreviewVisible] = useState(false);
  const [cardPreviewConfig, setCardPreviewConfig] = useState<any | null>(null);

  const hasSearchedUsers = useRef(false);

  const fetchTenants = async (params: { pageNum?: number; pageSize?: number } = {}) => {
    setLoading(true);
    try {
      const rawSearchParams = searchForm.getFieldsValue?.(true) || {};
      const searchParams = Object.keys(rawSearchParams).reduce((acc, key) => {
        const value = rawSearchParams[key];
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      const res = await tenantApi.getPageList({
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
        ...searchParams,
        ...params,
      });
      if (res.code === 200) {
        const list = res.data?.list || res.data?.records || [];
        setTenants(list);
        setPagination({
          ...pagination,
          total: res.data?.total || 0,
          ...(params.pageNum && { current: params.pageNum }),
          ...(params.pageSize && { pageSize: params.pageSize }),
        });
      }
    } catch (error) {
      MessagePlugin.error('获取租户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchPackageLevels = async () => {
    try {
      const res = await businessCardLevelApi.getPageList({ pageNum: 1, pageSize: 50, status: 1 });
      if (res.code === 200) {
        setPackageLevels(res.data?.list || res.data?.records || []);
      }
    } catch {
      console.error('获取套餐列表失败');
    }
  };

  const searchUsers = async (keyword: string) => {
    setUserLoading(true);
    try {
      const res = await userApi.getList({
        pageNum: 1,
        pageSize: 50,
        username: keyword || undefined,
      });
      if (res.code === 200) {
        setUsers(res.data?.list || res.data?.records || []);
      }
    } catch {
      console.error('获取用户列表失败');
    } finally {
      setUserLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
    fetchPackageLevels();
  }, []);

  const handleUserPopupVisibleChange = (visible: boolean) => {
    if (visible && !hasSearchedUsers.current) {
      hasSearchedUsers.current = true;
      searchUsers('');
    }
  };

  const handleUserSearch = (keyword: string) => {
    setUserSearchKeyword(keyword);
    searchUsers(keyword);
  };

  const resetFormState = () => {
    setCurrentTenant(null);
    setSelectedPackage(null);
    setFormTenantType(undefined);
    setFormTenantLevel(undefined);
    if (formInstanceRef.current) {
      formInstanceRef.current.reset();
    }
  };

  const resetMergeFormState = () => {
    setMergeSelectedPackage(null);
    setMergeAvatar('');
    setMergeFormTenantType(undefined);
    setMergeFormTenantLevel(undefined);
    if (mergeFormInstanceRef.current) {
      mergeFormInstanceRef.current.reset();
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchTenants({ pageNum: 1 });
  };

  const handleReset = () => {
    searchForm.reset();
    setPagination({ ...pagination, current: 1 });
    fetchTenants({ pageNum: 1 });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination({ ...pagination, current: page, pageSize });
    fetchTenants({ pageNum: page, pageSize });
  };

  const handleAdd = () => {
    resetFormState();
    hasSearchedUsers.current = false;
    setUsers([]);
    setVisible(true);
  };

  const handleMergeAdd = () => {
    resetMergeFormState();
    setMergeVisible(true);
  };

  const handleQuery = async (record: Tenant) => {
    setCardTenant(record);
    setCardSelectedRowKeys([]);
    setCardPagination({ current: 1, pageSize: 10, total: 0 });
    setCardDialogVisible(true);
    fetchCardList({ pageNum: 1, tenantId: record.id });
  };

  const fetchCardList = async (params: { pageNum?: number; pageSize?: number; tenantId?: number } = {}) => {
    setCardLoading(true);
    try {
      const res = await businessCardApi.getPageList({
        pageNum: params.pageNum ?? cardPagination.current,
        pageSize: params.pageSize ?? cardPagination.pageSize,
        tenantId: params.tenantId ?? cardTenant?.id,
      });
      if (res.code === 200) {
        setCardData(res.data?.list || []);
        setCardPagination((prev) => ({
          ...prev,
          total: res.data?.total || 0,
          ...(params.pageNum ? { current: params.pageNum } : null),
          ...(params.pageSize ? { pageSize: params.pageSize } : null),
        }));
      } else {
        MessagePlugin.error(res.message || '加载名片列表失败');
      }
    } catch {
      MessagePlugin.error('加载名片列表失败');
    } finally {
      setCardLoading(false);
    }
  };

  const handleCardPageChange = (pageInfo: { current: number; pageSize: number }) => {
    setCardPagination((prev) => ({ ...prev, current: pageInfo.current, pageSize: pageInfo.pageSize }));
    fetchCardList({ pageNum: pageInfo.current, pageSize: pageInfo.pageSize });
  };

  const handleCardUpdate = async (id: number) => {
    try {
      const res = await businessCardApi.getById(id);
      if (res.code === 200 && res.data) {
        setCardPreviewConfig(res.data);
        setCardPreviewVisible(true);
      } else {
        MessagePlugin.error(res.message || '获取名片详情失败');
      }
    } catch {
      MessagePlugin.error('获取名片详情失败');
    }
  };

  const handleCardDelete = async (id: number) => {
    try {
      const res = await businessCardApi.delete(id);
      if (res.code === 200) {
        MessagePlugin.success('删除成功');
        setCardSelectedRowKeys((prev) => prev.filter((key) => key !== id));
        fetchCardList();
      } else {
        MessagePlugin.error(res.message || '删除失败');
      }
    } catch {
      MessagePlugin.error('删除失败');
    }
  };

  const handleCardBatchDelete = async () => {
    if (cardSelectedRowKeys.length === 0) {
      MessagePlugin.warning('请先选择要删除的名片');
      return;
    }
    try {
      const res = await businessCardApi.deleteBatch(cardSelectedRowKeys as number[]);
      if (res.code === 200) {
        MessagePlugin.success('批量删除成功');
        setCardSelectedRowKeys([]);
        fetchCardList();
      } else {
        MessagePlugin.error(res.message || '批量删除失败');
      }
    } catch {
      MessagePlugin.error('批量删除失败');
    }
  };


  const handleEdit = async (record: Tenant) => {
    try {
      const res = await tenantApi.getById(record.id);
      if (res.code === 200) {
        const start = res.data?.startTime || '';
        const end = res.data?.endTime || '';
        const dateRange = (start || end) ? [start, end] : undefined;

        const pkg = packageLevels.find((p) => p.levelSalesPrice === res.data?.tenantPrice || p.id === res.data?.tenantLevel) || null;
        setSelectedPackage(pkg);

        setCurrentTenant({
          ...res.data,
          // 开关组件仅识别布尔值，统一做一次兼容转换
          websiteShow: toBoolFlag(res.data?.websiteShow),
          dateRange,
          userId: res.data?.userId,
        });
        setFormTenantType(res.data?.tenantType != null ? Number(res.data.tenantType) : undefined);
        setFormTenantLevel(res.data?.tenantLevel != null ? Number(res.data.tenantLevel) : undefined);
        setVisible(true);
      } else {
        MessagePlugin.error('获取租户详情失败');
      }
    } catch {
      MessagePlugin.error('获取租户详情失败');
    }
  };

  const handlePackageChange = (levelId: number) => {
    const pkg = packageLevels.find((p) => p.id === levelId);
    setSelectedPackage(pkg || null);
    if (pkg && formInstanceRef.current) {
      formInstanceRef.current.setFieldsValue({
        tenantLevel: levelId,
        tenantType: pkg.levelType,
        tenantPrice: pkg.levelSalesPrice,
        orgType: undefined,
        websiteShow: toBoolFlag(pkg.websiteShow),
      });
      setFormTenantType(pkg.levelType);
      setFormTenantLevel(levelId);
    }
  };

  const handleMergePackageChange = (levelId: number) => {
    const pkg = packageLevels.find((p) => p.id === levelId);
    setMergeSelectedPackage(pkg || null);
    if (pkg && mergeFormInstanceRef.current) {
      mergeFormInstanceRef.current.setFieldsValue({
        tenantLevel: levelId,
        tenantType: pkg.levelType,
        tenantPrice: pkg.levelSalesPrice,
        orgType: undefined,
        websiteShow: toBoolFlag(pkg.websiteShow),
      });
      setMergeFormTenantType(pkg.levelType);
      setMergeFormTenantLevel(levelId);
    }
  };

  /** 列表内直接切换「是否存在微官网」（仅套餐等级 5/6 支持） */
  const handleWebsiteShowToggle = async (record: Tenant, checked: boolean) => {
    if (!canSelectWebsiteShow(record.tenantLevel)) {
      MessagePlugin.warning('当前套餐等级不支持微官网');
      return;
    }
    setWebsiteTogglingId(record.id);
    try {
      const res = await tenantApi.update(record.id, { ...record, websiteShow: checked } as Partial<Tenant>);
      if (res.code === 200) {
        MessagePlugin.success(checked ? '已开启微官网' : '已关闭微官网');
        setTenants((prev) => prev.map((item) => (item.id === record.id ? { ...item, websiteShow: checked } : item)));
      } else {
        MessagePlugin.error(res.message || '操作失败');
      }
    } catch {
      MessagePlugin.error('操作失败，请稍后重试');
    } finally {
      setWebsiteTogglingId(null);
    }
  };

  const handleFormSubmit = async () => {
    try {
      const validateResult = formInstanceRef.current?.validate();
      if (validateResult !== true && Object.keys(validateResult).length > 0) {
        MessagePlugin.warning('请完善表单信息');
        return;
      }
      const values = formInstanceRef.current?.getFieldsValue(true);
      const { dateRange, tenantLevel, tenantType, tenantPrice, orgType, websiteShow, ...restValues } = values;

      const submitData: any = {
        ...restValues,
        tenantLevel: Number(tenantLevel),
        tenantType: Number(tenantType),
        // 组织类型仅企业租户（tenantType=1）需要；个人租户不提交
        orgType: Number(tenantType) === 1 && orgType != null && orgType !== '' ? Number(orgType) : undefined,
        // 微官网仅套餐等级为 5/6 时可选展示，其余套餐不提交该字段
        websiteShow: canSelectWebsiteShow(Number(tenantLevel)) ? Boolean(websiteShow) : undefined,
        tenantPrice: Number(tenantPrice),
        startTime: Array.isArray(dateRange) ? dateRange[0] : '',
        endTime: Array.isArray(dateRange) ? dateRange[1] : '',
      };

      if (currentTenant) {
        submitData.userId = currentTenant.userId;
        const res = await tenantApi.update(currentTenant.id, submitData);
        if (res.code === 200) {
          MessagePlugin.success('更新成功');
          setVisible(false);
          resetFormState();
          fetchTenants();
        } else {
          MessagePlugin.error(res.message || '操作失败');
        }
      } else {
        submitData.userId = Number(values.userId);
        const res = await tenantApi.create(submitData);
        if (res.code === 200) {
          MessagePlugin.success('创建成功');
          setVisible(false);
          resetFormState();
          fetchTenants();
        } else {
          MessagePlugin.error(res.message || '操作失败');
        }
      }
    } catch (error) {
      MessagePlugin.error('提交失败，请稍后重试');
    }
  };

  const handleMergeFormSubmit = async () => {
    try {
      const validateResult = mergeFormInstanceRef.current?.validate();
      if (validateResult !== true && Object.keys(validateResult).length > 0) {
        MessagePlugin.warning('请完善表单信息');
        return;
      }
      const values = mergeFormInstanceRef.current?.getFieldsValue(true);
      const { dateRange, tenantLevel, tenantType, tenantPrice, orgType, websiteShow, ...restValues } = values;

      const submitData: any = {
        ...restValues,
        avatar: mergeAvatar || '',
        tenantLevel: Number(tenantLevel),
        tenantType: Number(tenantType),
        // 组织类型仅企业租户（tenantType=1）需要；个人租户不提交
        orgType: Number(tenantType) === 1 && orgType != null && orgType !== '' ? Number(orgType) : undefined,
        // 微官网仅套餐等级为 5/6 时可选展示，其余套餐不提交该字段
        websiteShow: canSelectWebsiteShow(Number(tenantLevel)) ? Boolean(websiteShow) : undefined,
        tenantPrice: Number(tenantPrice),
        startTime: Array.isArray(dateRange) ? dateRange[0] : '',
        endTime: Array.isArray(dateRange) ? dateRange[1] : '',
      };

      const res = await tenantApi.createWithUser(submitData);
      if (res.code === 200) {
        MessagePlugin.success('合并创建成功');
        setMergeVisible(false);
        resetMergeFormState();
        fetchTenants();
      } else {
        MessagePlugin.error(res.message || '操作失败');
      }
    } catch (error) {
      MessagePlugin.error('提交失败，请稍后重试');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await tenantApi.delete(id);
      MessagePlugin.success('删除成功');
      setSelectedRowKeys([]);
      fetchTenants();
    } catch (error) {
      MessagePlugin.error('删除失败');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      MessagePlugin.warning('请先选择要删除的数据');
      return;
    }
    try {
      await tenantApi.deleteBatch(selectedRowKeys as number[]);
      MessagePlugin.success('批量删除成功');
      setSelectedRowKeys([]);
      fetchTenants();
    } catch (error) {
      MessagePlugin.error('批量删除失败');
    }
  };

  const toggleSelectRow = (rowKey: number) => {
    setSelectedRowKeys((prev) => {
      if (prev.includes(rowKey)) {
        return prev.filter((id) => id !== rowKey);
      }
      return [...prev, rowKey];
    });
  };

  const packageCard = useMemo(() => {
    if (!selectedPackage) return null;
    const { levelName, levelType, levelOrigPrice, levelSalesPrice, levelUserNum } = selectedPackage;
    const isEnterprise = levelType === 1;
    return (
      <div className="mb-4 rounded-xl overflow-hidden border shadow-sm" style={{
        borderColor: isEnterprise ? 'rgba(0,117,255,0.15)' : 'rgba(0,180,116,0.15)',
        background: isEnterprise
          ? 'linear-gradient(135deg, rgba(0,117,255,0.06), rgba(0,117,255,0.02))'
          : 'linear-gradient(135deg, rgba(0,180,116,0.06), rgba(0,180,116,0.02))',
      }}>
        <div className="flex items-center justify-between px-4 py-3" style={{
          background: isEnterprise
            ? 'linear-gradient(90deg, rgba(0,117,255,0.14), rgba(0,117,255,0))'
            : 'linear-gradient(90deg, rgba(0,180,116,0.14), rgba(0,180,116,0))',
        }}>
          <div className="flex items-center gap-2">
            <span className="w-2 h-6 rounded-full" style={{ background: isEnterprise ? '#0075ff' : '#00b474' }} />
            <div className="font-semibold text-base" style={{ color: isEnterprise ? '#0075ff' : '#00a86a' }}>
              {levelName}
            </div>
            <Tag theme={isEnterprise ? 'primary' : 'success'} variant="light" size="small">
              {isEnterprise ? '企业' : '个人'}
            </Tag>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 px-4 py-3">
          <div>
            <div className="text-xs text-gray-400 mb-0.5">原价</div>
            <div className="text-sm text-gray-400 line-through">¥{levelOrigPrice?.toFixed(2) || '0.00'}/年</div>
          </div>
          <div>
            <div className="text-xs text-gray-400 mb-0.5">售价</div>
            <div className="text-base font-semibold" style={{ color: isEnterprise ? '#0075ff' : '#00a86a' }}>
              ¥{levelSalesPrice?.toFixed(2) || '0.00'}/年
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400 mb-0.5">包含人数</div>
            <div className="text-sm font-medium text-gray-700">{levelUserNum} 人</div>
          </div>
        </div>
      </div>
    );
  }, [selectedPackage]);

  const mergePackageCard = useMemo(() => {
    if (!mergeSelectedPackage) return null;
    const { levelName, levelType, levelOrigPrice, levelSalesPrice, levelUserNum } = mergeSelectedPackage;
    const isEnterprise = levelType === 1;
    return (
      <div className="mb-4 rounded-xl overflow-hidden border shadow-sm" style={{
        borderColor: isEnterprise ? 'rgba(0,117,255,0.15)' : 'rgba(0,180,116,0.15)',
        background: isEnterprise
          ? 'linear-gradient(135deg, rgba(0,117,255,0.06), rgba(0,117,255,0.02))'
          : 'linear-gradient(135deg, rgba(0,180,116,0.06), rgba(0,180,116,0.02))',
      }}>
        <div className="flex items-center justify-between px-4 py-3" style={{
          background: isEnterprise
            ? 'linear-gradient(90deg, rgba(0,117,255,0.14), rgba(0,117,255,0))'
            : 'linear-gradient(90deg, rgba(0,180,116,0.14), rgba(0,180,116,0))',
        }}>
          <div className="flex items-center gap-2">
            <span className="w-2 h-6 rounded-full" style={{ background: isEnterprise ? '#0075ff' : '#00b474' }} />
            <div className="font-semibold text-base" style={{ color: isEnterprise ? '#0075ff' : '#00a86a' }}>
              {levelName}
            </div>
            <Tag theme={isEnterprise ? 'primary' : 'success'} variant="light" size="small">
              {isEnterprise ? '企业' : '个人'}
            </Tag>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 px-4 py-3">
          <div>
            <div className="text-xs text-gray-400 mb-0.5">原价</div>
            <div className="text-sm text-gray-400 line-through">¥{levelOrigPrice?.toFixed(2) || '0.00'}/年</div>
          </div>
          <div>
            <div className="text-xs text-gray-400 mb-0.5">售价</div>
            <div className="text-base font-semibold" style={{ color: isEnterprise ? '#0075ff' : '#00a86a' }}>
              ¥{levelSalesPrice?.toFixed(2) || '0.00'}/年
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400 mb-0.5">包含人数</div>
            <div className="text-sm font-medium text-gray-700">{levelUserNum} 人</div>
          </div>
          {/* <div>
            <div className="text-xs text-gray-400 mb-0.5">套餐编号</div>
            <div className="text-sm font-medium text-gray-700">#{mergeSelectedPackage.id}</div>
          </div> */}
        </div>
      </div>
    );
  }, [mergeSelectedPackage]);

  const cardColumns: PrimaryTableCol<any>[] = [
    {
      colKey: 'selection',
      title: () => (
        <input
          type="checkbox"
          checked={cardData.length > 0 && cardData.every((item) => cardSelectedRowKeys.includes(item.id))}
          onChange={(e) => {
            if (e.target.checked) {
              setCardSelectedRowKeys((prev) => Array.from(new Set([...prev, ...cardData.map((item) => item.id)])));
            } else {
              setCardSelectedRowKeys((prev) => prev.filter((id) => !cardData.some((item) => item.id === id)));
            }
          }}
          className="w-4 h-4 rounded border-gray-300"
        />
      ),
      width: 50,
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={cardSelectedRowKeys.includes(row.id)}
          onChange={() => {
            setCardSelectedRowKeys((prev) => prev.includes(row.id)
              ? prev.filter((id) => id !== row.id)
              : [...prev, row.id]);
          }}
          className="w-4 h-4 rounded border-gray-300"
        />
      ),
    },
    {
      colKey: 'name',
      title: '姓名',
      ellipsis: true,
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-800 truncate">{row.name || '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'phone',
      title: '手机号',
      ellipsis: true,
      cell: ({ row }: any) => (
        <div className="flex items-center gap-1 text-sm text-gray-700">
          <span className="truncate">{row.phone || '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'position',
      title: '职位',
      ellipsis: true,
      cell: ({ row }: any) => (
        <span className="text-sm text-gray-700 truncate block">{row.position || '-'}</span>
      ),
    },
    {
      colKey: 'email',
      title: '邮箱',
      ellipsis: true,
      cell: ({ row }: any) => (
        <div className="flex items-center gap-1 text-sm text-gray-700">
          <span className="truncate">{row.email || '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'company',
      title: '公司',
      ellipsis: true,
      cell: ({ row }: any) => (
        <div className="flex items-center gap-1 text-sm text-gray-700">
          <span className="truncate">{row.company || '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'address',
      title: '地址',
      ellipsis: true,
      cell: ({ row }: any) => (
        <div className="flex items-center gap-1 text-sm text-gray-700">
          <span className="truncate">{row.address || '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'status',
      title: '状态',
      width: 90,
      cell: ({ row }: any) => (
        <Tag theme={row.status === 1 ? 'success' : 'default'} variant="light">
          {row.status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      colKey: 'action',
      title: '操作',
      width: 120,
      cell: ({ row }: any) => (
        <Space>
          <Button theme="primary" variant="text" size="small" onClick={() => handleCardUpdate(row.id as number)}>查询</Button>
          <Popconfirm content="确定删除该名片吗？" onConfirm={() => handleCardDelete(row.id as number)}>
            <Button theme="danger" variant="text" size="small" icon={<Trash2 className="w-4 h-4" />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const columns: PrimaryTableCol<Tenant>[] = [
    {
      colKey: 'selection',
      title: () => (
        <input
          type="checkbox"
          checked={tenants.length > 0 && tenants.every((item) => selectedRowKeys.includes(item.id))}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRowKeys(tenants.map((o) => o.id));
            } else {
              setSelectedRowKeys([]);
            }
          }}
          className="w-4 h-4 rounded border-gray-300"
        />
      ),
      width: 50,
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={selectedRowKeys.includes(row.id)}
          onChange={() => toggleSelectRow(row.id)}
          className="w-4 h-4 rounded border-gray-300"
        />
      ),
    },
    {
      colKey: 'tenantName',
      title: '租户名称',
      width: 150,
      cell: ({ row }: { row: Tenant }) => row.tenantName || '-',
    },
    {
      colKey: 'tenantType',
      title: '租户类型',
      width: 100,
      cell: ({ row }: { row: Tenant }) => {
        const info = tenantTypeMap[row.tenantType];
        return info ? <Tag theme={info.theme} variant="light">{info.label}</Tag> : '-';
      },
    },
    {
      colKey: 'orgType',
      title: '组织类型',
      width: 100,
      cell: ({ row }: { row: Tenant }) => {
        // 仅企业租户（tenantType=1）区分企业/政府
        if (Number(row.tenantType) !== 1) return '-';
        const info = orgTypeMap[Number(row.orgType)];
        return info ? <Tag theme={info.theme} variant="light">{info.label}</Tag> : '-';
      },
    },
    {
      colKey: 'websiteShow',
      title: '是否存在微官网',
      width: 140,
      cell: ({ row }: { row: Tenant }) => {
        const show = toBoolFlag(row.websiteShow);
        // 仅套餐等级 5/6 支持微官网开关，其余套餐只展示状态
        if (!canSelectWebsiteShow(row.tenantLevel)) {
          return show ? <Tag theme="success" variant="light">存在</Tag> : <Tag theme="default" variant="light">不存在</Tag>;
        }
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Switch
              value={show}
              loading={websiteTogglingId === row.id}
              onChange={(value) => handleWebsiteShowToggle(row, Boolean(value))}
            />
          </div>
        );
      },
    },
    {
      colKey: 'tenantLevel',
      title: '套餐等级',
      width: 140,
      cell: ({ row }: { row: Tenant }) => {
        const pkg = packageLevels.find((p) => p.id === row.tenantLevel);
        if (pkg) {
          return <Tag theme={pkg.levelType === 1 ? 'primary' : 'success'} variant="light">{pkg.levelName}</Tag>;
        }
        const info = tenantTypeMap[row.tenantLevel];
        return info ? <Tag theme={(info as any).theme} variant="light">{info.label}</Tag> : '-';
      },
    },
    {
      colKey: 'tenantPrice',
      title: '租用价格',
      width: 120,
      cell: ({ row }: { row: Tenant }) => (
        <span className="font-medium text-[#1c5bff]">¥{row.tenantPrice?.toFixed(2) || '0.00'}</span>
      ),
    },
    {
      colKey: 'tenantStatus',
      title: '租户状态',
      width: 100,
      cell: ({ row }: any) => {
        const info = tenantStatusMap[row.tenantStatus];
        return info ? <Tag theme={info.theme} variant="light">{info.label}</Tag> : '-';
      },
    },
    // { colKey: 'userId', title: '关联用户', width: 120 },
    {
      colKey: 'invitedTime',
      title: '邀请时间',
      width: 170,
      cell: ({ row }: { row: Tenant }) => row.invitedTime || '-',
    },
    {
      colKey: 'startTime',
      title: '开始时间',
      width: 170,
      cell: ({ row }: { row: Tenant }) => row.startTime || '-',
    },
    {
      colKey: 'endTime',
      title: '结束时间',
      width: 170,
      cell: ({ row }: { row: Tenant }) => row.endTime || '-',
    },
    {
      colKey: 'action',
      title: '操作',
      width: 220,
      fixed: 'right',
      cell: ({ row }: { row: Tenant }) => (
        <Space>
          <Button theme="primary" variant="text" size="small" onClick={() => handleEdit(row)} icon={<EditIcon />}>
            编辑
          </Button>
          <Button theme="primary" variant="text" size="small" onClick={() => handleQuery(row)} icon={<EditIcon />}>
            名片
          </Button>
          <Popconfirm content="确定删除该租户吗？" onConfirm={() => handleDelete(row.id)}>
            <Button theme="danger" variant="text" size="small" icon={<DeleteIcon />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-bold">租户管理</h1>
        <Space>
          <Button theme="default" onClick={handleMergeAdd} icon={<UserAddIcon />}>
            合并创建
          </Button>
          <Button theme="primary" onClick={handleAdd} icon={<AddIcon />}>
            新增租户
          </Button>
          <Popconfirm content="确定批量删除选中的租户吗？" onConfirm={handleBatchDelete}>
            <Button theme="danger" disabled={selectedRowKeys.length === 0} icon={<DeleteIcon />}>
              批量删除
            </Button>
          </Popconfirm>
          {selectedRowKeys.length > 0 && (
            <span className="text-sm text-gray-500">
              已选择 <span className="text-[#1c5bff] font-medium">{selectedRowKeys.length}</span> 项
            </span>
          )}
        </Space>
      </div>

      {/* 搜索区域 */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
        <Form form={searchForm} layout="inline">
          <FormItem name="tenantType" label="租户类型">
            <Select placeholder="请选择租户类型" clearable style={{ width: 150 }}>
              <Option value={1}>企业</Option>
              <Option value={2}>个人</Option>
            </Select>
          </FormItem>
          <FormItem>
            <Button theme="primary" onClick={handleSearch} icon={<SearchIcon />}>
              查询
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={handleReset}>
              重置
            </Button>
          </FormItem>
        </Form>
      </div>

      {/* 表格区域 */}
      <div className="bg-white rounded-lg shadow-sm">
        <Table
          rowKey="id"
          data={tenants}
          columns={columns}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showJumper: true,
            onChange: (pageInfo) => handlePageChange(pageInfo.current, pageInfo.pageSize),
          }}
          selectOnRowClick
          selectedRowKeys={selectedRowKeys}
          onSelectChange={(val: any) => setSelectedRowKeys(val)}
        />
      </div>

      {/* 新增/编辑 弹窗 */}
      <Dialog
        header={currentTenant ? '编辑租户' : '新增租户'}
        visible={visible}
        onClose={() => {
          setVisible(false);
          resetFormState();
        }}
        onConfirm={handleFormSubmit}
        confirmBtn="保存"
        width={900}
        key={currentTenant ? `edit-${currentTenant.id}` : 'add-new'}
      >
        <Form
          ref={formInstanceRef}
          labelWidth={120}
          initialData={currentTenant || undefined}
        >
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            {!currentTenant && (
              <>
                <div className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100 flex items-center gap-2">
                  <PlusIcon style={{ color: '#0075ff' }} />
                  选择关联用户
                </div>
                <FormItem label="关联用户" name="userId" rules={[{ required: true, message: '请选择关联用户' }]}>
                  <Select
                    placeholder="请输入关键词搜索后选择用户"
                    filterable
                    loading={userLoading}
                    onSearch={handleUserSearch}
                    onPopupVisibleChange={handleUserPopupVisibleChange}
                    valueType="value"
                  >
                    {users.length === 0 && !userLoading ? (
                      <Option disabled value="" className="text-center text-gray-400">
                        请输入用户名搜索
                      </Option>
                    ) : (
                      users.map((u) => (
                        <Option key={u.id} value={u.id}>
                          <div className="flex items-center gap-2">
                            {/* <span className="font-medium">{u.nickname || u.username}</span> */}
                            <span className="text-xs text-gray-400">用户名：{u.username}</span>
                            {u.phone && <span className="text-xs text-gray-400">手机号：{u.phone}</span>}
                          </div>
                        </Option>
                      ))
                    )}
                  </Select>
                </FormItem>
              </>
            )}

            <div className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100 mt-4 flex items-center gap-2">
              <PlusIcon style={{ color: '#0075ff' }} />
              租户信息
            </div>
            <FormItem label="套餐等级" name="tenantLevel" rules={[{ required: true, message: '请选择套餐等级' }]}>
              <Select placeholder="请选择套餐等级" filterable onChange={handlePackageChange}>
                {packageLevels.map((p) => (
                  <Option key={p.id} value={p.id}>
                    {p.levelName}（{p.levelType === 1 ? '企业' : '个人'}，¥{p.levelSalesPrice}/年）
                  </Option>
                ))}
              </Select>
            </FormItem>

            {packageCard}

            <FormItem label="租户名称" name="tenantName" rules={[{ required: true, message: '请输入租户名称' }]}>
              <Input placeholder="请输入租户名称（企业名称等）" />
            </FormItem>

            <FormItem label="租户类型" name="tenantType" rules={[{ required: true, message: '请选择租户类型' }]}>
              <Select
                placeholder="请选择租户类型"
                disabled={!!selectedPackage}
                onChange={(value) => setFormTenantType(value == null ? undefined : Number(value))}
              >
                <Option value={1}>企业</Option>
                <Option value={2}>个人</Option>
              </Select>
            </FormItem>

            {Number(formTenantType) === 1 && (
              <FormItem label="组织类型" name="orgType" rules={[{ required: true, message: '请选择组织类型' }]}>
                <Select placeholder="请选择组织类型">
                  <Option value={1}>企业</Option>
                  <Option value={2}>政府</Option>
                </Select>
              </FormItem>
            )}
            {canSelectWebsiteShow(formTenantLevel) && (
              <FormItem label="微官网" name="websiteShow">
                <Switch />
              </FormItem>
            )}

            <FormItem label="租用价格" name="tenantPrice" rules={[{ required: true, message: '请选择套餐等级' }]}>
              <Input placeholder="选择套餐后自动填充" disabled value={selectedPackage ? String(selectedPackage.levelSalesPrice) : ''} />
            </FormItem>

            <FormItem label="邀请时间" name="invitedTime" rules={[{ required: true, message: '请选择邀请时间' }]}>
              <DatePicker placeholder="请选择邀请时间" format="YYYY-MM-DD HH:mm:ss" />
            </FormItem>

            <FormItem label="租用时间" name="dateRange" rules={[{ required: true, message: '请选择租用时间' }]}>
              <DateRangePicker
                placeholder={['开始时间', '结束时间']}
                format="YYYY-MM-DD HH:mm:ss"
              />
            </FormItem>
          </div>
        </Form>
      </Dialog>

      {/* 合并创建 弹窗 */}
      <Dialog
        header="合并创建（线下签约流程）"
        visible={mergeVisible}
        onClose={() => {
          setMergeVisible(false);
          resetMergeFormState();
        }}
        onConfirm={handleMergeFormSubmit}
        confirmBtn="保存"
        width={760}
        key="merge-create-new"
      >
        <Form
          ref={mergeFormInstanceRef}
          labelWidth={120}
          initialData={{ status: 1 }}
        >
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            <div className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100 flex items-center gap-2">
              <PlusIcon style={{ color: '#0075ff' }} />
              用户信息（新租户负责人）
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
              <FormItem label="头像" name="avatar" className="md:col-span-2">
                <CoverImageUploader 
                  simple
                  value={mergeAvatar}
                  onChange={(value) => setMergeAvatar(Array.isArray(value) ? value[0] : value)}
                />
              </FormItem>
              <FormItem label="用户名" name="username" rules={[{ required: true, message: '请输入用户名' }]}>
                <Input placeholder="请输入登录用户名" />
              </FormItem>
              <FormItem label="密码" name="password" rules={[{ required: true, message: '请输入密码' }]}>
                <Input placeholder="请输入初始密码" type="password" />
              </FormItem>
              <FormItem label="昵称" name="nickname">
                <Input placeholder="请输入昵称/姓名" />
              </FormItem>
              <FormItem label="手机号" name="phone" rules={[{ required: true, message: '请输入手机号' }]}>
                <Input placeholder="请输入手机号" />
              </FormItem>
              <FormItem label="邮箱" name="email">
                <Input placeholder="请输入邮箱" />
              </FormItem>
            </div>

            <div className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100 mt-4 flex items-center gap-2">
              <PlusIcon style={{ color: '#0075ff' }} />
              租户信息
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
              <FormItem label="套餐等级" name="tenantLevel" rules={[{ required: true, message: '请选择套餐等级' }]} className="md:col-span-2">
                <Select placeholder="请选择套餐等级" filterable onChange={handleMergePackageChange}>
                  {packageLevels.map((p) => (
                    <Option key={p.id} value={p.id}>
                      {p.levelName}（{p.levelType === 1 ? '企业' : '个人'}，¥{p.levelSalesPrice}/年）
                    </Option>
                  ))}
                </Select>
              </FormItem>
            </div>

            {mergePackageCard}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
              <FormItem label="租户名称" name="tenantName" rules={[{ required: true, message: '请输入租户名称' }]} className="md:col-span-2">
                <Input placeholder="请输入租户名称（企业名称等）" />
              </FormItem>
              <FormItem label="租户类型" name="tenantType" rules={[{ required: true, message: '请选择租户类型' }]}>
                <Select
                  placeholder="请选择租户类型"
                  disabled={!!mergeSelectedPackage}
                  onChange={(value) => setMergeFormTenantType(value == null ? undefined : Number(value))}
                >
                  <Option value={1}>企业</Option>
                  <Option value={2}>个人</Option>
                </Select>
              </FormItem>
              {Number(mergeFormTenantType) === 1 && (
                <FormItem label="组织类型" name="orgType" rules={[{ required: true, message: '请选择组织类型' }]}>
                  <Select placeholder="请选择组织类型">
                    <Option value={1}>企业</Option>
                    <Option value={2}>政府</Option>
                  </Select>
                </FormItem>
              )}
              {canSelectWebsiteShow(mergeFormTenantLevel) && (
              <FormItem label="微官网" name="websiteShow">
                <Switch />
              </FormItem>
            )}
              <FormItem label="租用价格" name="tenantPrice" rules={[{ required: true, message: '请选择套餐等级' }]}>
                <Input placeholder="选择套餐后自动填充" disabled value={mergeSelectedPackage ? String(mergeSelectedPackage.levelSalesPrice) : ''} />
              </FormItem>
              <FormItem label="邀请时间" name="invitedTime" rules={[{ required: true, message: '请选择邀请时间' }]} className="md:col-span-2">
                <DatePicker placeholder="请选择邀请时间" format="YYYY-MM-DD HH:mm:ss" />
              </FormItem>
              <FormItem label="租用时间" name="dateRange" rules={[{ required: true, message: '请选择租用时间' }]} className="md:col-span-2">
                <DateRangePicker
                  placeholder={['开始时间', '结束时间']}
                  format="YYYY-MM-DD HH:mm:ss"
                />
              </FormItem>
            </div>
          </div>
        </Form>
      </Dialog>

      {/* 电子名片管理 弹窗 */}
      <Dialog
        header={
          <div className="flex items-center gap-2">
            <span>电子名片管理</span>
            {cardTenant && (
              <span className="text-sm font-normal text-gray-500">
                · {cardTenant.tenantName}
              </span>
            )}
          </div>
        }
        visible={cardDialogVisible}
        onClose={() => {
          setCardDialogVisible(false);
          setCardSelectedRowKeys([]);
        }}
        footer={null}
        width={960}
        className="business-card-dialog"
      >
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {cardSelectedRowKeys.length > 0 && (
              <span className="text-sm text-gray-500">
                已选择 <span className="text-[#1c5bff] font-medium">{cardSelectedRowKeys.length}</span> 项
              </span>
            )}
            <Popconfirm content="确定批量删除选中的名片吗？" onConfirm={handleCardBatchDelete}>
              <Button theme="danger" disabled={cardSelectedRowKeys.length === 0} icon={<Trash2 className="w-4 h-4" />}>
                批量删除
              </Button>
            </Popconfirm>
          </div>

          {/* 表格区域 */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <Table
              rowKey="id"
              loading={cardLoading}
              columns={cardColumns}
              data={cardData}
              selectedRowKeys={cardSelectedRowKeys}
              onSelectChange={(keys: any) => setCardSelectedRowKeys(keys)}
              pagination={{
                current: cardPagination.current,
                pageSize: cardPagination.pageSize,
                total: cardPagination.total,
                showJumper: true,
                showPageSize: true,
                pageSizeOptions: [10, 20, 50],
                onChange: handleCardPageChange,
              }}
              bordered={false}
              hover
              size="small"
              tableLayout="fixed"
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        header="名片展示预览"
        visible={cardPreviewVisible}
        footer={null}
        width={440}
        onClose={() => setCardPreviewVisible(false)}
      >
        <>
          {cardPreviewConfig && (
            <div className="w-[350px] h-[210px] relative rounded-lg overflow-hidden shadow-lg" >
              <div className="absolute top-0 left-0 w-full h-full">
                <img src={cardPreviewConfig.backgroundImage} className="w-full h-full z-1" />
              </div>
              <div className="absolute top-0 left-0 z-10 w-full h-full" >
                <CardContentPreview config={cardPreviewConfig} />
              </div>
            </div>
          )}
        </>
      </Dialog>
    </div>
  );
}